import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Briefcase, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../context/AuthContext';
import { PacketActions, usePacketSummary } from './CpaPacketCard';
import { ReviewLinksList, useReviewLinks } from './ReviewLinksList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CpaContactCard = () => {
  const { user, refreshUser } = useAuth();
  const summary = usePacketSummary();
  const packetYear = summary?.year;
  const { links, reload } = useReviewLinks();
  const [name, setName] = useState(user?.cpa_name || '');
  const [email, setEmail] = useState(user?.cpa_email || '');
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/user/cpa-contact`, { cpa_name: name.trim() || null, cpa_email: email.trim() || null }, { withCredentials: true });
      toast.success(email.trim() ? 'CPA contact saved' : 'CPA contact removed');
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.status === 422 ? 'Please enter a valid email address' : 'Could not save CPA contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-soft border-border/40" data-testid="cpa-contact-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5" /> Your CPA</CardTitle>
        <CardDescription>Save your CPA's details to email advisor chats to them in one tap.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="cpa-name-setting">CPA name</Label>
            <Input id="cpa-name-setting" maxLength={100} placeholder="Pat Lee, CPA" value={name} onChange={(e) => setName(e.target.value)} data-testid="cpa-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cpa-email-setting">CPA email</Label>
            <Input id="cpa-email-setting" type="email" placeholder="cpa@example.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="cpa-email-input" />
          </div>
          <Button type="submit" disabled={saving} data-testid="cpa-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
          </Button>
        </form>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stone-200 bg-stone-50 p-4" data-testid="settings-cpa-packet">
          <div>
            <p className="text-sm font-semibold text-stone-900">Year-End CPA Packet{packetYear ? ` · ${packetYear} tax year` : ''}</p>
            <p className="text-xs text-stone-500">Summary, savings tracker, strategy report and all advisor chats in one PDF.</p>
          </div>
          {summary && <PacketActions summary={summary} testPrefix="settings-packet" onSent={reload} />}
        </div>
        <ReviewLinksList links={links} reload={reload} />
      </CardContent>
    </Card>
  );
};

export default CpaContactCard;
